/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Ladda from 'ladda';
import _ from 'lodash';
import Backbone from 'backbone';
import { View } from 'backbone.marionette';

import DocumentDownloadTypes from '@/enums/DocumentDownloadTypes';
import AttachmentsCollectionView from '@/views/AttachmentsCollectionView';
import GroupAttachmentCardsCollectionView from '@/views/GroupAttachmentCardsCollectionView';
import AWPackageFileUploadTmpl from '@/fileupload/AWPackageFileUploadTmpl.hbs';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';
import ZipAWPackageAttachments from '../../services/ZipAWPackageAttachments';
import DownloadDocumentFromFS from '../../../common/util/downloadDocumentFromFS';

import tmplAWPackageAttachmentsView from './AWPackageAttachmentsView.hbs';

const AWPackageAttachmentsView = View.extend({
  template: tmplAWPackageAttachmentsView,

  className: 'aw-package-attachments-view',

  ui: {
    attachments: '.attachments',
    awPackageAttachments: '.aw-package-attachments',
    errRequired: '.err-required',
    zipButton: '#zipAndDownload'
  },

  regions: {
    attachments: '@ui.attachments',
    awPackageAttachments: '@ui.awPackageAttachments'
  },

  events: {
    'click @ui.zipButton': 'zipAndDownloadAWPackageAttachments'
  },

  childEvents: {
    'has:errors': 'onHasErrors',
    'no:errors': 'onNoErrors'
  },

  onRender() {
    this.showAWPackageLevelAttachments();
    this.showOrderLevelAttachments();
    this.ui.errRequired.hide();
  },

  onHasErrors() {
    this.trigger('has:errors');
  },

  onNoErrors() {
    this.trigger('no:errors');
  },

  showAWPackageLevelAttachments() {
    const awPackageID = this.model.get('attachments').awPackageId;
    const isReadOnly = !this.options.editable;
    const awPackageDocumentTypes = _.filter(this.model.get('awPackageDocumentTypes'), (documentType) => {
      return documentType.documentTypeId !== '21' && documentType.documentTypeId !== '22';
    });
    const attachments = this.model.get('attachments').awPackageAttachments;
    if (awPackageDocumentTypes.length >= 1) {
      const documentMapping = _.map(awPackageDocumentTypes, (documentType) => {
        const { documentTypeDescription, documentTypeId, attachmentId, defaultFileName } = documentType;
        const attachment = _.find(attachments, (attachment) => attachment.documentTypeId === documentTypeId) || null;
        const isRequired = documentType?.isRequired === '1';

        return {
          label: {
            text: documentTypeDescription,
            fileName: documentType.defaultFileName,
            isDocument: documentType.attachmentId,
            downloadType: DocumentDownloadTypes.TEMPLATE,
            attachmentId
          },
          attachment: {
            isReadOnly: true,
            attachmentName: attachment?.fileName,
            attachmentId: attachment?.id,
            fileUploadTmpl: AWPackageFileUploadTmpl,
            pathToFileId: 'html.head.script.Envelope.Body.UploadAWPackageAttachmentResponse.AttachmentID.__text',
            defaultFileName,
            validator: ValidFileTypeUtil,
            metadata: {
              attachmentId: attachment?.id,
              fileName: attachment?.fileName,
              awPackageId: awPackageID,
              documentTypeId
            }
          },
          isRequired,
          isReadOnly
        };
      });

      const collection = new Backbone.Collection(documentMapping);
      this.attachmentsCollectionView = new AttachmentsCollectionView({
        collection
      });
      this.showChildView('awPackageAttachments', this.attachmentsCollectionView);
    }
  },

  showOrderLevelAttachments() {
    const numOfOrderPackages = this.model.get('attachments').orderPackages.length;
    if (numOfOrderPackages >= 1) {
      _.each(this.model.get('attachments').orderPackages, (orderPackageAttachments, index) => {
        orderPackageAttachments.documentTypes = this.model.get('documentTypes');
        if (index === numOfOrderPackages - 1) {
          orderPackageAttachments.lastOrderPackage = true;
        }
      });
      const collection = new Backbone.Collection(this.model.get('attachments').orderPackages);
      _.each(collection.models, (model) => model.set('isReadOnly', !this.model.get('editable')));
      _.each(collection.models, (model) =>
        model.set('isSpecialtyRequirementEditable', this.isSpecialtyRequirementEditable())
      );

      this.orderPackageCollectionView = new GroupAttachmentCardsCollectionView({
        collection
      });
      this.showChildView('attachments', this.orderPackageCollectionView);
    }
  },

  isSpecialtyRequirementEditable() {
    return this.options.editable;
  },

  validate() {
    if (!this.attachmentsCollectionView) return true;

    const views = this.attachmentsCollectionView.children._views;
    for (const attachmentItemView of views) {
      if (!attachmentItemView.validate()) {
        this.ui.errRequired.show();
        this.trigger('has:errors');
        return false;
      }
    }
    this.ui.errRequired.hide();
    this.trigger('no:errors');
    return true;
  },

  async zipAndDownloadAWPackageAttachments(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.zipButton[0]);
    spinner.start();
    const awPackageAttachments = await ZipAWPackageAttachments(this.model.get('attachments').awPackageId);
    DownloadDocumentFromFS({
      tempFileName: awPackageAttachments.zipFileName,
      fileName: `AW Package - ${this.model.get('attachments').awPackageId}.zip`
    });
    spinner.stop();
  }
});

export default AWPackageAttachmentsView;
